import {
  differenceInDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns'
import memoizeOne from 'memoize-one'
import pluralize from 'pluralize'
import z from 'zod'

export namespace Timespan {
  export const Preset = z.enum([
    'Today',
    'Yesterday',
    'ThisWeek',
    'LastWeek',
    'Last7Days',
    'Last14Days',
    'ThisMonth',
    'LastMonth',
    'Last3Months',
    'Last6Months',
    'Last12Months',
  ])

  export type Preset = z.infer<typeof Preset>

  export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6

  export const StaticTimespan = z.object({
    type: z.literal('static'),
    start: z
      .string()
      .datetime()
      .transform((val) => new Date(val))
      .or(z.date()),
    end: z
      .string()
      .datetime()
      .transform((val) => new Date(val))
      .or(z.date()),
  })

  export type StaticTimespan = z.infer<typeof StaticTimespan>

  export const DynamicTimespan = z.object({
    type: z.literal('preset'),
    value: Preset,
  })

  export type DynamicTimespan = z.infer<typeof DynamicTimespan>

  export const DEFAULT_TIMESPAN_LAST_7_DAYS = {
    type: 'preset',
    value: 'Last7Days',
  } satisfies DynamicTimespan

  export const DEFAULT_TIMESPAN_LAST_3_MONTHS = {
    type: 'preset',
    value: 'Last3Months',
  } satisfies DynamicTimespan

  export const Info = StaticTimespan.or(DynamicTimespan)

  export type Info = z.infer<typeof Info>

  export const toStaticTimespan = memoizeOne(
    (input: Info | null, weekStartsOn?: Timespan.DayOfWeek): StaticTimespan => {
      try {
        switch (input?.type) {
          case 'static':
            return StaticTimespan.parse(input)
          case 'preset':
            return generateStaticTimestampFromPreset(input.value, weekStartsOn)
          default:
            throw new Error(`Invalid timespan: ${JSON.stringify(input)}`)
        }
      } catch (e) {
        console.error('Invalid timespan:', JSON.stringify(input))
        console.error(e)
        return todayTimespan()
      }
    },
  )

  export const differenceInDaysWithTimespan = (
    timespan: Info | null,
    weekStartsOn?: Timespan.DayOfWeek,
  ): number => {
    if (timespan === null) return Infinity
    const range = toStaticTimespan(timespan, weekStartsOn)
    return Math.abs(differenceInDays(range.start, range.end))
  }

  const generateStaticTimestampFromPreset = (
    preset: Preset,
    weekStartsOn: Timespan.DayOfWeek = 1,
  ): StaticTimespan => {
    switch (preset) {
      case 'Today':
        return todayTimespan()
      case 'Yesterday':
        return yesterdayTimespan()
      case 'ThisWeek':
        return wtdTimespan(todayTimespan(), weekStartsOn)
      case 'Last7Days':
        return last7Days()
      case 'Last14Days':
        return last14Days()
      case 'LastWeek':
        return lastWeekTimespan(todayTimespan(), weekStartsOn)
      case 'ThisMonth':
        return mtdTimespan()
      case 'LastMonth':
        return lastMonthTimespan()
      case 'Last3Months':
        return last3MonthsTimespan()
      case 'Last6Months':
        return last6MonthsTimespan()
      case 'Last12Months':
        return last12MonthsTimespan()
      default:
        throw new Error(`Invalid preset: ${preset}`)
    }
  }

  export const humanize = memoizeOne((range: { start: Date; end?: Date }) => {
    if (!range.end && !range.start) {
      return ''
    }

    const now = new Date()
    const yesterday = subDays(now, 1)

    const timespanDistance = range.end
      ? differenceInDays(range.end, range.start) + 1
      : Infinity
    const isEndSameDay = range.end ? isSameDay(range.end, range.start) : false
    const isYesterdayEndSameDay = range.end ? isSameDay(range.end, yesterday) : false

    if (isSameDay(range.start, now) && isEndSameDay) {
      return 'Today'
    }

    if (isSameDay(range.start, yesterday) && isYesterdayEndSameDay) {
      return 'Yesterday'
    }

    if (timespanDistance <= 7 && range.end && isSameDay(range.end, subDays(now, 1))) {
      return `Last ${timespanDistance} ${pluralize('day', timespanDistance)}`
    }

    if (timespanDistance === 1) {
      return `${format(range.start, 'PP')}`
    }

    return `${format(range.start, 'PP')} ${range.end ? ` – ${format(range.end, 'PP')}` : ''}`
  })

  const todayTimespan = (today = new Date()): StaticTimespan => ({
    type: 'static',
    start: startOfDay(today),
    end: endOfDay(today),
  })
  const yesterdayTimespan = (
    today: StaticTimespan = todayTimespan(),
  ): StaticTimespan => ({
    type: 'static',
    start: startOfDay(subDays(today.start, 1)),
    end: endOfDay(subDays(today.end, 1)),
  })
  const lastWeekTimespan = (
    timespan: StaticTimespan = todayTimespan(),
    weekStartsOn: Timespan.DayOfWeek = 1,
  ): StaticTimespan => ({
    type: 'static',
    start: startOfWeek(subWeeks(timespan.start, 1), { weekStartsOn }),
    end: endOfWeek(subWeeks(timespan.start, 1), { weekStartsOn }),
  })
  const last3MonthsTimespan = (
    timespan: StaticTimespan = todayTimespan(),
  ): StaticTimespan => ({
    type: 'static',
    start: startOfMonth(subMonths(startOfMonth(timespan.start), 3)),
    end: endOfMonth(subMonths(timespan.start, 1)),
  })
  const lastMonthTimespan = (
    timespan: StaticTimespan = yesterdayTimespan(),
  ): StaticTimespan => ({
    type: 'static',
    start: startOfMonth(subMonths(timespan.start, 1)),
    end: endOfMonth(subMonths(timespan.end, 1)),
  })
  const last7Days = (timespan: StaticTimespan = yesterdayTimespan()): StaticTimespan => ({
    type: 'static',
    start: startOfDay(subDays(timespan.start, 6)),
    end: timespan.end,
  })
  const last14Days = (
    timespan: StaticTimespan = yesterdayTimespan(),
  ): StaticTimespan => ({
    type: 'static',
    start: startOfDay(subDays(timespan.start, 13)),
    end: timespan.end,
  })
  const wtdTimespan = (
    timespan: StaticTimespan = todayTimespan(),
    weekStartsOn: Timespan.DayOfWeek,
  ): StaticTimespan => ({
    type: 'static',
    start: startOfWeek(timespan.start, { weekStartsOn }),
    end: timespan.end,
  })
  const mtdTimespan = (timespan: StaticTimespan = todayTimespan()): StaticTimespan => ({
    type: 'static',
    start: startOfMonth(timespan.start),
    end: timespan.end,
  })
  const last6MonthsTimespan = (
    timespan: StaticTimespan = todayTimespan(),
  ): StaticTimespan => ({
    type: 'static',
    start: startOfMonth(subMonths(startOfMonth(timespan.start), 6)),
    end: endOfMonth(subMonths(timespan.start, 1)),
  })
  const last12MonthsTimespan = (
    timespan: StaticTimespan = todayTimespan(),
  ): StaticTimespan => ({
    type: 'static',
    start: startOfMonth(subMonths(startOfMonth(timespan.start), 12)),
    end: endOfMonth(subMonths(timespan.start, 1)),
  })
}
